import React from "react"

import { Checkbox } from "@coval/ui/checkbox"
import { RadioGroup, RadioGroupItem } from "@coval/ui/radio-group"

import type {
  AudioFilterSpeaker,
  AudioSpeakerFilterConfig,
} from "@/types/metrics/metadata"

import { AUDIO_SPEAKERS } from "../types"

interface SpeakerFilterEditorProps {
  filter: AudioSpeakerFilterConfig
  onChange: (filter: AudioSpeakerFilterConfig) => void
  // Render as single-select radios; replaces the previous speaker on pick.
  singleSelect?: boolean
  // Restrict the picker to "agent" only (e.g. metrics that only analyze the
  // agent side). Implies singleSelect.
  agentOnly?: boolean
}

export const SpeakerFilterEditor: React.FC<SpeakerFilterEditorProps> = ({
  filter,
  onChange,
  singleSelect,
  agentOnly,
}) => {
  const speakers = agentOnly
    ? AUDIO_SPEAKERS.filter((speaker) => speaker.value === "agent")
    : AUDIO_SPEAKERS

  if (singleSelect || agentOnly) {
    const selected = filter.speakers[0] ?? "agent"
    return (
      <RadioGroup
        value={selected}
        onValueChange={(value) =>
          onChange({ ...filter, speakers: [value as AudioFilterSpeaker] })
        }
        className="flex items-center gap-x-4"
        aria-label="Speaker selection"
      >
        {speakers.map((speaker) => (
          <label
            key={speaker.value}
            className="flex items-center gap-2 text-sm cursor-pointer"
          >
            <RadioGroupItem
              value={speaker.value}
              aria-label={`Use ${speaker.label} as the speaker filter`}
            />
            <span>{speaker.label}</span>
          </label>
        ))}
      </RadioGroup>
    )
  }

  const handleToggle = (speaker: AudioFilterSpeaker, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...filter.speakers, speaker]))
      : filter.speakers.filter((s) => s !== speaker)
    // Schema requires at least one speaker — ignore a toggle that would empty the list.
    if (next.length === 0) {
      return
    }
    onChange({ ...filter, speakers: next })
  }

  return (
    <fieldset className="flex items-center gap-x-4 border-0 p-0 m-0">
      <legend className="sr-only">Speaker selection</legend>
      {speakers.map((speaker) => {
        const checked = filter.speakers.includes(speaker.value)
        return (
          <label
            key={speaker.value}
            className="flex items-center gap-2 text-sm cursor-pointer"
          >
            <Checkbox
              checked={checked}
              onCheckedChange={(v) => handleToggle(speaker.value, v === true)}
              aria-label={`Include ${speaker.label} in speaker filter`}
            />
            <span>{speaker.label}</span>
          </label>
        )
      })}
    </fieldset>
  )
}
